// src/components/InboxScreen.jsx
//
// Triage for escalations arriving from Practice Comms. Governed by
// functions/CONTRACT.md.
//
// WHAT A ROW SHOWS, AND WHY IT IS SO LITTLE. A title, whether it is urgent, and
// how long it has been waiting. That is the entire non-PHI payload — four
// fields, two of which are opaque ids. Practice Brain holds no patient data and
// this screen is where that boundary is felt, so if a title turns out to be too
// thin to triage on, the fix is a better title at approval time in Comms. Not a
// wider payload, and specifically not the deep link that used to be sent: it
// resolves into a PHI-bearing system, and handing this app a route into patient
// data undermines the boundary even though the URL carries none (CONTRACT.md §6).
//
// TWO THINGS THIS SCREEN IS CAREFUL ABOUT:
//
//   1. It says "handed off" only once `itemId` exists. Accepting sets a status;
//      a trigger creates the item and stamps that id. Between those writes the
//      document claims acceptance with nothing behind it, and getTask reports
//      `pending` for exactly that reason. A person deserves the same honesty as
//      the other system — a confirmation the mechanism has not earned is a false
//      record either way.
//   2. It enforces the rejection reason (1-280) in the form. The security rules
//      refuse anything outside that, and a rules refusal arrives as an opaque
//      permission error with nothing naming the limit. That exact shape — a
//      server bound the form did not know about — has already cost this project
//      a debugging session on the Comms side.

import { useMemo, useState } from "react";
import { acceptIncoming, rejectIncoming, correctIncomingTriage } from "../lib/db";

const REJECTION_REASON_MAX = 280; // CONTRACT.md §4

const inputStyle = {
  width:"100%", background:"#0F172A", border:"1px solid #2D3748",
  color:"#F1F5F9", padding:"9px 12px", borderRadius:8, fontSize:14,
  outline:"none", fontFamily:"inherit", boxSizing:"border-box"
};
const labelStyle = {
  display:"block", fontSize:11, fontWeight:700, color:"#64748B",
  textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:5
};

/** How long an escalation has been sitting here. The only time information a
 *  triager gets, since nothing else about the item is visible to them. */
function waitingFor(createdAt) {
  if (!createdAt?.toDate) return "just now";
  const ms = Date.now() - createdAt.toDate().getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function Pill({ children, color, bg, border }) {
  return (
    <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",
      padding:"2px 7px",borderRadius:6,color,background:bg,border:`1px solid ${border}`}}>
      {children}
    </span>
  );
}

/** One pending escalation, with its two decisions. */
function PendingRow({ entry, members, categories, member, showToast }) {
  const [mode, setMode] = useState(null); // null | "accept" | "reject"
  const [busy, setBusy] = useState(false);
  // Pre-filled from the entry, which matters for a stalled acceptance: the
  // triager already chose these once, and making them start over would hide
  // which field the trigger actually objected to.
  const [accept, setAccept] = useState({
    assignedTo: entry.assignedTo || "",
    due: entry.due || "",
    category: entry.category || "",
  });
  const [reason, setReason] = useState("");

  // An accepted escalation with no itemId yet. Three different situations wear
  // that same shape, and telling them apart matters:
  //
  //   inFlight  the trigger is working on it, a second or two. Saying anything
  //             alarming here would be a false failure claim - the mirror image
  //             of the false success this screen exists to avoid.
  //   failed    the trigger refused the triage fields and said why.
  //   slow      no error, no item, and time has passed. Something did not run.
  //             Rarer, and the one that would otherwise be invisible, since
  //             there is no error message to display.
  const unfinished = entry.status === "accepted" && !entry.itemId;
  const failed = unfinished && !!entry.triageError;
  const waitedMs = entry.triagedAt?.toMillis ? Date.now() - entry.triagedAt.toMillis() : 0;
  const slow = unfinished && !failed && waitedMs > 120000;
  const needsFixing = failed || slow;
  const inFlight = unfinished && !needsFixing;

  const reasonTooLong = reason.length > REJECTION_REASON_MAX;
  const canReject = reason.trim().length > 0 && !reasonTooLong;
  const canAccept = accept.assignedTo && accept.due && accept.category;

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      showToast(msg);
      setMode(null);
    } catch (e) {
      showToast(e.message || "That did not go through", "error");
    }
    setBusy(false);
  };

  const doAccept = () => run(
    () => (unfinished
      ? correctIncomingTriage(entry.id, accept)
      : acceptIncoming(entry.id, { ...accept, triagedBy: member.uid })),
    // Not "handed off" — the item does not exist yet. The trigger creates it,
    // and the row will say so once itemId lands.
    "Accepted — creating the item",
  );

  const doReject = () => run(
    () => rejectIncoming(entry.id, { reason: reason.trim(), triagedBy: member.uid }),
    "Rejected — sent back to Practice Comms with your reason",
  );

  return (
    <div style={{background:"#1A2332",border:`1px solid ${needsFixing ? "#7F1D1D" : "#2D3748"}`,
      borderRadius:12,padding:14,marginBottom:10}}>

      <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:8}}>
        <div style={{flex:1,fontSize:15,fontWeight:600,color:"#F1F5F9",lineHeight:1.4}}>
          {entry.title}
        </div>
        {entry.urgent && <Pill color="#FCA5A5" bg="#7F1D1D33" border="#7F1D1D">Urgent</Pill>}
      </div>

      <div style={{fontSize:12,color:"#64748B",marginBottom:unfinished?10:12}}>
        Waiting {waitingFor(entry.createdAt)} · from Practice Comms
      </div>

      {/* Not "handed off". The item does not exist until the trigger stamps
          itemId, and this row will say so when it does — the same rule getTask
          follows, for the same reason. */}
      {inFlight && (
        <div style={{background:"#1E293B",border:"1px solid #2D3748",borderRadius:8,
          padding:"9px 12px",marginBottom:4,fontSize:13,color:"#94A3B8"}}>
          Accepted — creating the item…
        </div>
      )}

      {needsFixing && (
        <div style={{background:"#7F1D1D22",border:"1px solid #7F1D1D",borderRadius:8,
          padding:"10px 12px",marginBottom:12,fontSize:13,color:"#FCA5A5",lineHeight:1.5}}>
          <strong>This acceptance did not complete.</strong>
          <div style={{marginTop:4,color:"#FCA5A5CC"}}>
            {entry.triageError
              || "The item was never created, and nothing reported why."}
          </div>
          <div style={{marginTop:6,color:"#94A3B8"}}>
            No item exists and Practice Comms is still waiting on this. Check the details below
            and try again.
          </div>
        </div>
      )}

      {mode === null && !unfinished && (
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setMode("accept")} disabled={busy}
            style={{background:"#134E4A",border:"1px solid #2DD4BF44",color:"#5EEAD4",
              padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
            Accept
          </button>
          <button onClick={()=>setMode("reject")} disabled={busy}
            style={{background:"transparent",border:"1px solid #2D3748",color:"#94A3B8",
              padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            Decline
          </button>
        </div>
      )}

      {(mode === "accept" || needsFixing) && (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <label style={labelStyle}>Assign to</label>
            <select style={inputStyle} value={accept.assignedTo}
              onChange={e=>setAccept(a=>({...a,assignedTo:e.target.value}))}>
              <option value="">Choose a team member…</option>
              {members.filter(m=>m.permission!=="external").map(m=>(
                <option key={m.uid||m.id} value={m.uid||m.id}>{m.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Due</label>
            <input type="date" style={inputStyle} value={accept.due}
              onChange={e=>setAccept(a=>({...a,due:e.target.value}))}/>
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={accept.category}
              onChange={e=>setAccept(a=>({...a,category:e.target.value}))}>
              <option value="">Choose a category…</option>
              {categories.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={doAccept} disabled={busy || !canAccept}
              style={{background:canAccept?"#134E4A":"#1E293B",
                border:`1px solid ${canAccept?"#2DD4BF44":"#2D3748"}`,
                color:canAccept?"#5EEAD4":"#475569",
                padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:700,
                cursor:canAccept?"pointer":"not-allowed"}}>
              {needsFixing ? "Try again" : "Accept and create the item"}
            </button>
            {!needsFixing && (
              <button onClick={()=>setMode(null)} disabled={busy}
                style={{background:"transparent",border:"none",color:"#64748B",
                  padding:"8px 10px",fontSize:13,cursor:"pointer"}}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {mode === "reject" && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div>
            <label style={labelStyle}>Why are you declining it?</label>
            <textarea style={{...inputStyle,resize:"vertical"}} rows={3} value={reason}
              onChange={e=>setReason(e.target.value)}
              maxLength={REJECTION_REASON_MAX}
              placeholder="This goes back to the Practice Comms front desk."/>
            {/* The counter is not decoration. The rules refuse an empty or
                over-length reason, and that refusal reaches the user as a bare
                permission error. */}
            <div style={{fontSize:11,marginTop:4,
              color:reasonTooLong?"#FCA5A5":"#64748B"}}>
              {reason.length}/{REJECTION_REASON_MAX}
              {reason.trim().length===0 && " — a reason is required"}
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={doReject} disabled={busy || !canReject}
              style={{background:canReject?"#7F1D1D":"#1E293B",
                border:`1px solid ${canReject?"#EF444444":"#2D3748"}`,
                color:canReject?"#FCA5A5":"#475569",
                padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:700,
                cursor:canReject?"pointer":"not-allowed"}}>
              Decline and send back
            </button>
            <button onClick={()=>setMode(null)} disabled={busy}
              style={{background:"transparent",border:"none",color:"#64748B",
                padding:"8px 10px",fontSize:13,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** An escalation that has been decided. Kept visible because a rejection's
 *  reason is what the Practice Comms front desk is now reading, and whoever
 *  wrote it should be able to see what they sent. */
function DecidedRow({ entry, members }) {
  const who = members.find(m => (m.uid||m.id) === entry.triagedBy)?.displayName;
  const assignee = members.find(m => (m.uid||m.id) === entry.assignedTo)?.displayName;

  return (
    <div style={{background:"#151E2D",border:"1px solid #1E293B",borderRadius:10,
      padding:"11px 13px",marginBottom:8}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:5}}>
        <div style={{flex:1,fontSize:13,color:"#94A3B8",lineHeight:1.4}}>{entry.title}</div>
        {entry.status === "accepted"
          ? <Pill color="#5EEAD4" bg="#134E4A44" border="#134E4A">Handed off</Pill>
          : <Pill color="#94A3B8" bg="#2D374844" border="#2D3748">Declined</Pill>}
      </div>
      <div style={{fontSize:11,color:"#475569"}}>
        {entry.status === "accepted"
          ? <>Assigned to {assignee || "a team member"}{entry.due ? ` · due ${entry.due}` : ""}</>
          : <>Reason sent back: {entry.rejectionReason}</>}
        {who ? ` · by ${who}` : ""}
      </div>
    </div>
  );
}

export function InboxScreen({ pending, decided, members, categories, member, onClose, showToast }) {
  const [showDecided, setShowDecided] = useState(false);

  // Oldest first: the queue is a waiting line, and the thing waiting longest is
  // the thing most likely to be a problem. Sorted here rather than in the query
  // so the inbox needs no composite index to work.
  const queue = useMemo(
    () => [...pending].sort((a,b) =>
      (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0)),
    [pending],
  );

  // An accepted escalation with no item yet is not pending, but it is also not
  // finished, and it belongs at the top of the queue rather than filed under
  // decided where nobody would look. Most are in flight for a second; the ones
  // that are not are the whole reason this list exists.
  const unfinished = useMemo(
    () => decided.filter(d => d.status === "accepted" && !d.itemId),
    [decided],
  );

  const settled = useMemo(
    () => decided
      .filter(d => !(d.status === "accepted" && !d.itemId))
      .sort((a,b) => (b.triagedAt?.toMillis?.() ?? 0) - (a.triagedAt?.toMillis?.() ?? 0))
      .slice(0, 25),
    [decided],
  );

  return (
    <div style={{position:"fixed",inset:0,background:"#0F172A",zIndex:900,overflowY:"auto",
      paddingBottom:"calc(40px + env(safe-area-inset-bottom))"}}>
      <div style={{maxWidth:640,margin:"0 auto",padding:"20px 16px"}}>

        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.2em",color:"#2DD4BF",
              textTransform:"uppercase",marginBottom:4}}>Practice Comms</div>
            <div style={{fontSize:22,fontWeight:800,color:"#F1F5F9"}}>Incoming</div>
          </div>
          <button onClick={onClose}
            style={{background:"transparent",border:"1px solid #2D3748",color:"#94A3B8",
              padding:"7px 13px",borderRadius:8,fontSize:13,cursor:"pointer"}}>
            Close
          </button>
        </div>

        <div style={{fontSize:12,color:"#64748B",lineHeight:1.6,marginBottom:20}}>
          Escalations from the front desk, waiting for someone to take them on. Accepting one
          creates a normal item; declining sends your reason back and leaves the work with them.
          No patient information crosses — a title and an urgency flag is all there is.
        </div>

        {unfinished.map(entry => (
          <PendingRow key={entry.id} entry={entry} members={members}
            categories={categories} member={member} showToast={showToast}/>
        ))}

        {queue.length === 0 && unfinished.length === 0 ? (
          <div style={{textAlign:"center",padding:"48px 20px",color:"#475569",fontSize:14}}>
            Nothing waiting.
          </div>
        ) : (
          queue.map(entry => (
            <PendingRow key={entry.id} entry={entry} members={members}
              categories={categories} member={member} showToast={showToast}/>
          ))
        )}

        {settled.length > 0 && (
          <div style={{marginTop:26}}>
            <button onClick={()=>setShowDecided(s=>!s)}
              style={{background:"transparent",border:"none",color:"#64748B",fontSize:12,
                fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",
                cursor:"pointer",padding:"6px 0",marginBottom:8}}>
              {showDecided ? "▾" : "▸"} Recently decided ({settled.length})
            </button>
            {showDecided && settled.map(entry => (
              <DecidedRow key={entry.id} entry={entry} members={members}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
